// Route tables for the app's navigation and for pages split into subtabs
// (feedback #55). Kept free of React so the matching rules can be tested
// directly.
//
// Slice 2 of the redesign (docs/2026-10-03-ui-build-handoff.md): four
// destinations, organized by moment rather than by object, shown as a
// bottom tab bar on a phone and top tabs on desktop. Everything else lives
// behind one menu ("Me" on a phone, "Settings and account" on desktop).

export type NavIcon = "home" | "plan" | "groceries" | "recipes";

export interface NavItem {
  to: string;
  label: string;
  // Match only this exact path, not paths beneath it (an index subtab).
  end?: boolean;
}

export interface TabItem extends NavItem {
  icon: NavIcon;
}

export interface MenuItem extends NavItem {
  // Shown only to app admins (0051). Hiding is cosmetic; RLS is what
  // actually protects the data.
  adminOnly?: boolean;
}

// URLs are unchanged from before the redesign (/grocery, not /groceries), so
// bookmarks and links already sent keep working.
export const TABS: TabItem[] = [
  { to: "/home", label: "Home", icon: "home" },
  { to: "/plan", label: "Plan", icon: "plan" },
  { to: "/grocery", label: "Groceries", icon: "groceries" },
  { to: "/recipes", label: "Recipes", icon: "recipes" },
];

// Where the app opens, and where an unknown or forbidden URL lands.
export const HOME_PATH = "/home";

// Behind the menu. Household and "Your color" live on the Plan Defaults page
// until settings get their own design (handoff, "Do not build yet" 7).
// Inventory and the two purveyor pages are admin-only until Home leads their
// release (decided 2026-09-28; handoff slice 3) -- drop the flag to release
// one to everyone.
export const MENU_ITEMS: MenuItem[] = [
  { to: "/defaults", label: "Plan Defaults and household" },
  { to: "/inventory", label: "Inventory", adminOnly: true },
  { to: "/purveyors", label: "Purveyors", adminOnly: true },
  { to: "/purveyor-portal", label: "Purveyor Portal", adminOnly: true },
  { to: "/admin", label: "Admin", adminOnly: true },
];

// The menu items this user should see.
export function visibleMenuItems(isAdmin: boolean, items: MenuItem[] = MENU_ITEMS): MenuItem[] {
  return items.filter((item) => !item.adminOnly || isAdmin);
}

// Subtabs of the Plan page. `end` marks the index tab, which must match its
// path exactly -- otherwise "/plan" would also count as active on
// "/plan/shared".
export const PLAN_SUBTABS: NavItem[] = [
  { to: "/plan", label: "Our plan", end: true },
  { to: "/plan/shared", label: "Shared with us" },
  { to: "/plan/public", label: "Public plans" },
];

// Subtabs of the Purveyor Portal (feedback #57, #58).
export const PORTAL_SUBTABS: NavItem[] = [
  { to: "/purveyor-portal", label: "Boxes", end: true },
  { to: "/purveyor-portal/catalog", label: "Catalog" },
  { to: "/purveyor-portal/profile", label: "Profile" },
];

// Pages that used to be top-level and now live as subtabs. Their old URLs
// redirect, so bookmarks and links already sent to people keep working.
export const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: "/shared-with-us", to: "/plan/shared" },
  { from: "/public-plans", to: "/plan/public" },
];

// Whether `pathname` is at `to` or somewhere beneath it. A plain prefix test
// would put a future "/planner" inside "/plan", so the next character must
// be a slash or the end.
export function isPathWithin(pathname: string, to: string, end = false): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === to) return true;
  return !end && path.startsWith(`${to}/`);
}

// The item whose section contains `pathname`, or undefined.
export function activeNavItem<T extends NavItem>(pathname: string, items: T[]): T | undefined {
  return items.find((item) => isPathWithin(pathname, item.to, item.end));
}
